import { FormEvent, useState } from "react";
import { api, formatCnpj } from "../api";
import type { Certificate, Company, Team } from "../types";

type Flash = { kind: "ok" | "error"; text: string } | null;
type Props = {
  items: Company[];
  teams: Team[];
  certificates: Certificate[];
  reload: () => void;
  flash: (value: Flash) => void;
};
type CompanyForm = Omit<Company, "id">;

const numberOrNull = (value: string) => (value === "" ? null : Number(value));

export default function CompaniesPage({
  items,
  teams,
  certificates,
  reload,
  flash,
}: Props) {
  const empty = (): CompanyForm => ({
    legal_name: "",
    trade_name: "",
    cnpj: "",
    team_id: teams[0]?.id ?? 0,
    responsible_user_id: null,
    certificate_id: null,
    search_start_day: 15,
    search_time: "08:00",
    competence_offset_months: null,
    recurrence_days: null,
    processing_priority: null,
    pdf_retention_days: null,
    active: true,
    fgts_enabled: false,
    notes: "",
  });
  const [form, setForm] = useState<CompanyForm>(empty);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const shown = items.filter((item) =>
    `${item.legal_name} ${item.trade_name} ${item.cnpj}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );

  function create() {
    setEditingId(null);
    setForm(empty());
    setOpen(true);
  }
  function edit(company: Company) {
    const { id, ...values } = company;
    setEditingId(id);
    setForm(values);
    setOpen(true);
  }
  async function save(event: FormEvent) {
    event.preventDefault();
    try {
      await api(editingId ? `/api/companies/${editingId}` : "/api/companies", {
        method: editingId ? "PUT" : "POST",
        body: JSON.stringify(form),
      });
      setOpen(false);
      flash({
        kind: "ok",
        text: editingId
          ? "Configurações da empresa atualizadas."
          : "Empresa cadastrada.",
      });
      reload();
    } catch (error) {
      flash({
        kind: "error",
        text:
          error instanceof Error ? error.message : "Falha ao salvar empresa",
      });
    }
  }
  async function importFile(file?: File) {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    try {
      const result = await api<{ created: number; updated: number }>(
        "/api/companies/import",
        { method: "POST", body },
      );
      flash({
        kind: "ok",
        text: `Importação concluída: ${result.created} criadas e ${result.updated} atualizadas.`,
      });
      reload();
    } catch (error) {
      flash({
        kind: "error",
        text: error instanceof Error ? error.message : "Falha na importação",
      });
    }
  }
  return (
    <>
      <div className="page-head">
        <div>
          <p className="eyebrow">CADASTROS</p>
          <h1>Empresas</h1>
          <p>
            Cadastre a empresa e seu certificado. A participação nos grupos é definida em Aplicar grupos.
          </p>
        </div>
        <div className="actions">
          <label className="secondary upload">
            Importar planilha
            <input
              type="file"
              accept=".xlsx,.csv"
              onChange={(event) => importFile(event.target.files?.[0])}
            />
          </label>
          <button className="primary" onClick={create}>
            + Nova empresa
          </button>
        </div>
      </div>
      <div className="toolbar">
        <input
          className="search"
          placeholder="Buscar empresa ou CNPJ"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <a className="text-link" href="/api/companies/import-template">
          Baixar modelo
        </a>
      </div>
      <section className="card table-card">
        {shown.length ? (
          <table>
            <thead>
              <tr>
                <th>Empresa</th>
                <th>CNPJ</th>
                <th>Certificado</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((company) => (
                <tr key={company.id}>
                  <td>
                    <strong>{company.trade_name || company.legal_name}</strong>
                    <small>
                      {teams.find((team) => team.id === company.team_id)?.name}
                    </small>
                  </td>
                  <td>{formatCnpj(company.cnpj)}</td>
                  <td>
                    {certificates.find(
                      (item) => item.id === company.certificate_id,
                    )?.name ?? <span className="red">Não definido</span>}
                  </td>
                  <td>
                    <span
                      className={`pill ${company.active ? "active" : "inactive"}`}
                    >
                      {company.active ? "Ativa" : "Inativa"}
                    </span>
                  </td>
                  <td>
                    <button
                      className="table-action"
                      onClick={() => edit(company)}
                    >
                      Editar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty">
            <span>◎</span>
            <p>Nenhuma empresa encontrada</p>
          </div>
        )}
      </section>
      {open && (
        <div
          className="modal-backdrop"
          onMouseDown={(event) =>
            event.target === event.currentTarget && setOpen(false)
          }
        >
          <section className="modal company-modal">
            <header>
              <div>
                <p className="eyebrow">EMPRESA</p>
                <h2>
                  {editingId ? "Editar configurações" : "Cadastrar empresa"}
                </h2>
              </div>
              <button className="icon-button" onClick={() => setOpen(false)}>
                ×
              </button>
            </header>
            <form className="form-grid" onSubmit={save}>
              <label className="wide">
                Razão social
                <input
                  required
                  value={form.legal_name}
                  onChange={(event) =>
                    setForm({ ...form, legal_name: event.target.value })
                  }
                />
              </label>
              <label>
                Nome fantasia
                <input
                  value={form.trade_name}
                  onChange={(event) =>
                    setForm({ ...form, trade_name: event.target.value })
                  }
                />
              </label>
              <label>
                CNPJ
                <input
                  required
                  disabled={editingId !== null}
                  value={form.cnpj}
                  placeholder="00.000.000/0000-00"
                  onChange={(event) =>
                    setForm({ ...form, cnpj: event.target.value })
                  }
                />
              </label>
              <label>
                Equipe
                <select
                  value={form.team_id}
                  onChange={(event) =>
                    setForm({ ...form, team_id: Number(event.target.value) })
                  }
                >
                  {teams.map((team) => (
                    <option value={team.id} key={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Certificado
                <select
                  value={form.certificate_id ?? ""}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      certificate_id: numberOrNull(event.target.value),
                    })
                  }
                >
                  <option value="">Selecione</option>
                  {certificates.map((certificate) => (
                    <option key={certificate.id} value={certificate.id}>
                      {certificate.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="check-label">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(event) =>
                    setForm({ ...form, active: event.target.checked })
                  }
                />
                Empresa ativa
              </label>
              <label className="wide">
                Observações
                <textarea
                  value={form.notes}
                  onChange={(event) =>
                    setForm({ ...form, notes: event.target.value })
                  }
                />
              </label>
              <footer className="wide">
                <button
                  type="button"
                  className="secondary"
                  onClick={() => setOpen(false)}
                >
                  Cancelar
                </button>
                <button className="primary">Salvar configurações</button>
              </footer>
            </form>
          </section>
        </div>
      )}
    </>
  );
}
